// AuthContext.tsx (cleaned-up version)
import { useCallback, useEffect, useRef, useState } from "react";
import AuthContext from "./AuthContextInstance";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export type User = {
  id: number;
  name: string;
  role: "admin" | "staff";
};

export type AuthContextType = {
  user: User | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
  logout: () => void;
  manualLogoutRef: React.MutableRefObject<boolean>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const manualLogoutRef = useRef(false);
  const isFirstSessionCheck = useRef(true);

  const logout = useCallback(() => {
    setUser(null);
    if (!manualLogoutRef.current && !isFirstSessionCheck.current) {
      toast({
        title: "You have been logged out",
        description: "Please log in again.",
        variant: "destructive",
      });
    }
    manualLogoutRef.current = false;
    navigate("/", { replace: true });
  }, [navigate]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/me`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.user.id,
          name:
            data.user.name ||
            (data.user.role === "admin" ? "Administrator" : "Staff Member"),
          role: data.user.role,
        });

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(logout, SESSION_TIMEOUT);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
      if (isFirstSessionCheck.current) isFirstSessionCheck.current = false;
    }
  }, [logout]);

  useEffect(() => {
    refreshSession();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [refreshSession]);

  //console.log("AuthContext user:", user);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        refreshSession,
        logout,
        manualLogoutRef,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
